// ============================================
// SEAFARER EDIT PAGE — Sadece durum + doğrulama yönetimi
// ============================================

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { ArrowLeft, Save, ShieldCheck, User, MapPin, Award } from 'lucide-react';
import { useTranslation } from '@crew-at-sea/i18n';
import { PageHeader } from '@/components';
import { adminApi } from '@/services/api';
import { cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/seafarers/$id/edit')({
  component: SeafarerEditPage,
});

function SeafarerEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['panel', 'common', 'enums']);

  const [isVerified, setIsVerified] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: seafarerData, isLoading } = useQuery({
    queryKey: ['admin', 'seafarers', id],
    queryFn: () => adminApi.getSeafarer(id),
  });

  const seafarer = (seafarerData as any)?.data || seafarerData;

  useEffect(() => {
    if (seafarer) {
      setIsVerified(seafarer.isVerified || false);
    }
  }, [seafarer]);

  // Doğrulama güncelleme mutation
  const verifyMutation = useMutation({
    mutationFn: (verified: boolean) => adminApi.verifySeafarer(id, verified),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'seafarers'] });
      navigate({ to: `/seafarers/${id}` });
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || t('panel:seafarers.updateError') });
    },
  });

  // Durum güncelleme mutation
  const statusMutation = useMutation({
    mutationFn: ({ status, reason }: { status: string; reason?: string }) =>
      adminApi.updateUserStatus(seafarer?.userId, { status: status as any, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'seafarers'] });
      navigate({ to: `/seafarers/${id}` });
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || t('panel:seafarers.updateError') });
    },
  });

  const handleSaveVerification = () => {
    verifyMutation.mutate(isVerified);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
      </div>
    );
  }

  const userStatus = (seafarer as any)?.user?.status || 'ACTIVE';

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:seafarers.editTitle')}
        description={seafarer ? `${seafarer.firstName} ${seafarer.lastName}` : ''}
        actions={
          <Link
            to={`/seafarers/${id}` as any}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-maritime hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common:back')}
          </Link>
        }
      />

      {/* Denizci bilgi özeti (read-only) */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          {t('panel:seafarers.seafarer')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">{t('panel:seafarers.firstName')} / {t('panel:seafarers.lastName')}</p>
              <p className="font-medium text-gray-900">{seafarer?.firstName} {seafarer?.lastName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Award className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">{t('panel:seafarers.department')}</p>
              <p className="font-medium text-gray-900">
                {t(`enums:department.${seafarer?.department}`, { defaultValue: seafarer?.department || '-' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">{t('panel:seafarers.location')}</p>
              <p className="font-medium text-gray-900">
                {seafarer?.city && `${seafarer.city}, `}{seafarer?.country || seafarer?.nationality || '-'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Award className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">{t('panel:seafarers.experience')}</p>
              <p className="font-medium text-gray-900">
                {t('panel:seafarers.yearsExp', { count: seafarer?.experienceYears || 0 })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Doğrulama yönetimi */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          {t('panel:seafarers.verification')}
        </h3>
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-maritime">
          <div className="flex items-center gap-3">
            <ShieldCheck className={cn('h-6 w-6', isVerified ? 'text-green-500' : 'text-gray-400')} />
            <div>
              <p className="font-medium text-gray-900">{t('panel:seafarers.verifiedSeafarer')}</p>
              <p className="text-sm text-gray-500">
                {isVerified ? 'Bu denizci doğrulanmış durumda' : 'Bu denizci henüz doğrulanmamış'}
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isVerified}
            onClick={() => setIsVerified(!isVerified)}
            className={cn(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              isVerified ? 'bg-green-500' : 'bg-gray-300'
            )}
          >
            <span className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              isVerified ? 'translate-x-6' : 'translate-x-1'
            )} />
          </button>
        </div>

        {errors.submit && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-maritime">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSaveVerification}
            disabled={verifyMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {verifyMutation.isPending ? t('common:saving') : t('common:save')}
          </button>
        </div>
      </div>

      {/* Hesap durumu yönetimi */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Hesap Durumu</h3>
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-maritime">
          <div>
            <p className="font-medium text-gray-900">Mevcut Durum</p>
            <p className={cn(
              'text-sm font-medium mt-1',
              userStatus === 'ACTIVE' && 'text-green-600',
              userStatus === 'SUSPENDED' && 'text-red-600',
              userStatus === 'INACTIVE' && 'text-gray-500',
            )}>
              {userStatus === 'ACTIVE' ? 'Aktif' : userStatus === 'SUSPENDED' ? 'Askıya Alındı' : 'Pasif'}
            </p>
          </div>
          <div className="flex gap-2">
            {userStatus === 'SUSPENDED' ? (
              <button
                onClick={() => statusMutation.mutate({ status: 'ACTIVE' })}
                disabled={statusMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-maritime hover:bg-green-700 disabled:opacity-50 text-sm"
              >
                {t('panel:seafarers.activate')}
              </button>
            ) : (
              <button
                onClick={() => statusMutation.mutate({ status: 'SUSPENDED' })}
                disabled={statusMutation.isPending}
                className="px-4 py-2 bg-amber-600 text-white rounded-maritime hover:bg-amber-700 disabled:opacity-50 text-sm"
              >
                {t('panel:seafarers.suspend')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
